import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  governanceApi,
  workspacesApi,
  type EscalationItem,
  type QuestionItem,
  type ShadowDecision,
} from '../services/api'

/**
 * One definition of "waiting on you", for the whole shell.
 *
 * Before this hook the topbar bell counted `escalations + questions` while
 * Today counted `shadowDecisions + escalations + questions`, so in shadow mode
 * the bell read 0 while the same screen's header read 12. Today never moved
 * onto it — it kept its own fetches, and read held drafts whatever the mode —
 * and was retired instead (MAR-309). `AppShell` calls this ONCE; the bell, the
 * Inbox drawer and Ledger's waiting/Shadow banners all read that one instance
 * (screens through `useNeedsYouState` in ShellContext), so there is one fetch,
 * one number and one refresh interval.
 *
 * It returns the items as well as the count: the Inbox drawer renders from the
 * same response rather than re-fetching what the badge already has.
 *
 * Escalations and questions are two endpoints over one shared `notifications`
 * table, split only by a `type` filter server-side. They are merged back
 * together here. A combined pending endpoint would make this one round trip —
 * see docs/work/follow-ups.md F-14.
 *
 * Two properties this hook has to hold, both learned the hard way:
 *
 * 1. **A failed read is not an empty queue.** Blanking a category on `catch`
 *    made a transient 5xx indistinguishable from "nothing waiting", which
 *    would hide a governance decision from the merchant.
 * 2. **Retained data belongs to the workspace it came from.** Because (1)
 *    keeps data across a failure, that data must never outlive a workspace
 *    switch — otherwise workspace A's queue renders under B, and acting on a
 *    row would send B's workspace id with A's notification id.
 */
export interface NeedsYou {
  escalations: EscalationItem[]
  questions: QuestionItem[]
  /** Drafts held by Shadow. Empty unless the workspace is in shadow mode. */
  shadow: ShadowDecision[]
  /**
   * More held drafts exist than the `SHADOW_LIMIT` shown — so `shadow.length`,
   * and therefore `total`, is a floor: "20+", never "20". Scoped exactly as
   * `shadow` is (false whenever `shadow` is masked), and on a failed refresh it
   * keeps the last successful read's value, as `shadow` keeps its items.
   */
  shadowHasMore: boolean
  total: number
  /**
   * `total` as every surface prints it: `"21+"` when the held-drafts page was
   * full (`shadowHasMore`), so the bell, the Inbox and the Ledger banner state
   * the same floor rather than one of them saying "21+" and the rest "21".
   * Print this, never `total` itself; compare and pluralise on `total`.
   */
  totalLabel: string
  /** A request is in flight (initial load or a refresh). */
  loading: boolean
  /**
   * Categories not yet read for the current workspace *and mode*. Distinct
   * from `stale`: not asked yet, rather than asked and failed. Both mean
   * `total` is a floor.
   */
  pending: Category[]
  /**
   * Nothing at all is known for the current scope — every category is pending.
   * `total === 0 && initialising` must never be presented as "nothing waiting".
   */
  initialising: boolean
  /**
   * Categories whose most recent read failed. Their arrays hold the last
   * successful response, or are empty if there has never been one — so
   * `total` is a floor, not a count, whenever this is non-empty.
   */
  stale: Category[]
  /** Any category failed to refresh: the queue may hold more than is shown. */
  degraded: boolean
  /**
   * A category failed and has never loaded for this scope, so nothing is known
   * about it.
   */
  unknown: boolean
  /**
   * WHICH categories `unknown` is about — stale and never loaded for this
   * scope. Lets a surface that describes only some categories (Ledger's
   * waiting banner leaves held drafts to the Shadow banner) state its own
   * honest state rather than inherit another category's failure.
   */
  unknownIn: Category[]
  /** `pending` or `stale` is non-empty: what is shown is not the whole queue. */
  incomplete: boolean
  refresh: () => void
}

export type Category = 'escalations' | 'questions' | 'shadow'

const CATEGORIES: Category[] = ['escalations', 'questions', 'shadow']
const LIVE_CATEGORIES: Category[] = ['escalations', 'questions']

/**
 * The one empty array this hook ever returns. `AppShell` memoises the shell
 * context on this hook's result, so a fresh `[]` per render — while the
 * snapshot is out of scope, or the shadow half is masked after a mode flip —
 * would hand every screen a new context value on every unrelated render (each
 * streamed Ask token). Frozen so no caller can push into the shared instance.
 */
const EMPTY = Object.freeze([]) as unknown as never[]

const REFRESH_MS = 60_000
const SHADOW_LIMIT = 20

/**
 * A snapshot carries the scope it describes. `shadowMode` is part of that
 * scope, not just `workspaceId`: the shadow array is only meaningful for the
 * mode it was fetched under. Live→shadow must read as unread rather than
 * empty, and shadow→live must drop the held drafts the instant the mode
 * changes, not when the next request happens to return.
 */
interface Snapshot {
  workspaceId: string | undefined
  shadowMode: boolean | null
  escalations: EscalationItem[]
  questions: QuestionItem[]
  shadow: ShadowDecision[]
  shadowHasMore: boolean
  stale: Category[]
  loadedOnce: Category[]
}

const emptyFor = (workspaceId: string | undefined): Snapshot => ({
  workspaceId,
  shadowMode: null,
  escalations: EMPTY,
  questions: EMPTY,
  shadow: EMPTY,
  shadowHasMore: false,
  stale: EMPTY,
  loadedOnce: EMPTY,
})

/**
 * A category list rebuilt by `.filter` on every render, held at one identity
 * for as long as its contents are unchanged.
 */
function useStableCategories(list: Category[]): Category[] {
  const key = list.join(',')
  return useMemo(() => (key ? (key.split(',') as Category[]) : EMPTY), [key])
}

/**
 * ⚠️ `deps` is spread into a dependency array, so it must be a **fixed-length**
 * list of primitives for the lifetime of a given caller. React compares deps
 * positionally; a variable-length array is undefined behaviour, and the
 * exhaustive-deps rule is disabled below so the linter will not catch it.
 */
export function useNeedsYou(
  workspaceId: string | undefined,
  isShadow: boolean,
  deps: unknown[] = []
): NeedsYou {
  const [snapshot, setSnapshot] = useState<Snapshot>(() => emptyFor(workspaceId))
  const [loading, setLoading] = useState(false)
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  // Validated at render, not in an effect. Clearing in an effect still permits
  // one render of the previous scope's queue, which is one render too many: the
  // drawer would disclose A's customer questions under B, and resolving one
  // would post B's workspace id with A's notification id.
  const scoped = snapshot.workspaceId === workspaceId ? snapshot : emptyFor(workspaceId)

  // The shadow array belongs to the mode it was fetched under. On shadow→live
  // the held drafts stop applying the instant the mode flips — not when the
  // next request returns — and on live→shadow they are unread, not absent.
  const shadowApplies = scoped.shadowMode === isShadow
  const { escalations, questions } = scoped
  const shadow = shadowApplies ? scoped.shadow : EMPTY
  const shadowHasMore = shadowApplies && scoped.shadowHasMore
  const loadedOnce = shadowApplies ? scoped.loadedOnce : scoped.loadedOnce.filter((c) => c !== 'shadow')
  const stale = useStableCategories(shadowApplies ? scoped.stale : scoped.stale.filter((c) => c !== 'shadow'))

  // In live mode there are no held drafts by definition, so `shadow` is not a
  // category awaiting a read — only in shadow mode does it have to be fetched
  // before the queue can be called complete.
  const required = isShadow ? CATEGORIES : LIVE_CATEGORIES
  const pending = useStableCategories(
    workspaceId ? required.filter((c) => !loadedOnce.includes(c) && !stale.includes(c)) : EMPTY
  )
  const initialising = !!workspaceId && pending.length === required.length
  const unknownIn = useStableCategories(stale.filter((c) => !loadedOnce.includes(c)))

  useEffect(() => {
    if (!workspaceId) {
      setSnapshot(emptyFor(undefined))
      return
    }
    let alive = true
    // Interval ticks overlap: a slow request fired at t=60s can resolve after
    // the t=120s one and overwrite fresher data with older. Only the newest
    // load started is allowed to write.
    let latest = 0
    const load = async () => {
      const seq = ++latest
      setLoading(true)
      const [esc, qs, sh] = await Promise.all([
        governanceApi.getEscalations(workspaceId, 'pending').catch(() => null),
        governanceApi.getQuestions(workspaceId, 'pending').catch(() => null),
        // One past the limit. The server accumulates matches across pages and
        // then slices to `limit`, so on its final page it can cut matches and
        // still return no `nextKey` — the extra row is what reveals them.
        isShadow
          ? workspacesApi
              .getShadowDecisions(workspaceId, { status: 'pending', limit: SHADOW_LIMIT + 1 })
              .catch(() => null)
          : Promise.resolve(null),
      ])
      if (!alive || seq !== latest) return

      setSnapshot((prev) => {
        // Never merge onto another scope's snapshot. A mode flip invalidates
        // only the shadow half, so the rest is kept — a mode change should not
        // blank the whole inbox.
        const sameWorkspace = prev.workspaceId === workspaceId
        const base: Snapshot = sameWorkspace
          ? prev.shadowMode === isShadow
            ? prev
            : {
                ...prev,
                shadow: [],
                shadowHasMore: false,
                loadedOnce: prev.loadedOnce.filter((c) => c !== 'shadow'),
                stale: prev.stale.filter((c) => c !== 'shadow'),
              }
          : emptyFor(workspaceId)
        const failed: Category[] = []
        const loaded = new Set(base.loadedOnce)

        // On success take the new list; on failure keep what we last had and
        // record the category as stale, so the count reads as a floor.
        const keep = <T,>(name: Category, ok: boolean, next: T[], last: T[]): T[] => {
          if (!ok) {
            failed.push(name)
            return last
          }
          loaded.add(name)
          return next
        }

        const nextEscalations = keep('escalations', !!esc?.success, esc?.data?.escalations || EMPTY, base.escalations)
        const nextQuestions = keep('questions', !!qs?.success, qs?.data?.questions || EMPTY, base.questions)
        // Not being in shadow mode is a real empty, not a failed read.
        const shadowOk = isShadow ? !!sh?.success : true
        const raw = sh?.data?.decisions || EMPTY
        const nextShadow = isShadow
          ? keep('shadow', shadowOk, raw.length > SHADOW_LIMIT ? raw.slice(0, SHADOW_LIMIT) : raw, base.shadow)
          : keep('shadow', true, EMPTY, base.shadow)
        // Travels with `shadow`: the new read's flag on success, the last one's
        // on failure — a failed refresh must not turn "20+" back into "20".
        // More exist when the extra row came back, or when the server stopped
        // with pages unscanned (`nextKey`). The cursor alone can over-state —
        // the unscanned pages may hold no further pending draft — but "20+" is
        // still a true floor, and the same rule as merchant-reads/attention.js.
        const nextHasMore = isShadow
          ? shadowOk
            ? raw.length > SHADOW_LIMIT || !!sh?.data?.nextKey
            : base.shadowHasMore
          : false

        return {
          workspaceId,
          shadowMode: isShadow,
          escalations: nextEscalations,
          questions: nextQuestions,
          shadow: nextShadow,
          shadowHasMore: nextHasMore,
          stale: failed.length ? failed : EMPTY,
          loadedOnce: [...loaded],
        }
      })
      setLoading(false)
    }
    void load()
    const timer = window.setInterval(load, REFRESH_MS)
    return () => {
      alive = false
      window.clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, isShadow, nonce, ...deps])

  // One identity for as long as every field holds: `AppShell` memoises the
  // shell context on this object, so a new one per render re-renders every
  // screen reading it. Every input above is a primitive, a snapshot array, the
  // shared EMPTY, or a key-stabilised category list.
  return useMemo(() => {
    const total = escalations.length + questions.length + shadow.length
    return {
      escalations,
      questions,
      shadow,
      shadowHasMore,
      total,
      totalLabel: `${total}${shadowHasMore ? '+' : ''}`,
      loading,
      pending,
      initialising,
      stale,
      degraded: stale.length > 0,
      unknown: unknownIn.length > 0,
      unknownIn,
      incomplete: !!workspaceId && (pending.length > 0 || stale.length > 0),
      refresh,
    }
  }, [escalations, questions, shadow, shadowHasMore, loading, pending, initialising, stale, unknownIn, workspaceId, refresh])
}
